import type { DaisySize } from '@trimble-oss/moduswebcomponents';
import { Component, JSXElement, onCleanup, onMount } from 'solid-js';
import { MODUS_USER_INFO_STYLESHEET } from '../../mc-custom-css/mc-userinfo.css';
import { injectInRenderContext } from '../util';

export interface UserInfoUser {
  firstName?: string;
  lastName?: string;
  email?: string;
  thumbnail?: string;
}

export interface UserInfoProps {
  user: UserInfoUser;
  defaultImg: string;
  size?: 'micro' | 'small' | 'medium' | 'large';
  onClick?(): void;
  'data-cy'?: string;
}

/** Maps UserInfo size to Modus Avatar (see Modus Web Components Avatar). */
const avatarSizeMap: Record<string, 'sm' | 'md' | 'lg'> = {
  micro: 'sm',
  small: 'sm',
  medium: 'md',
  large: 'lg',
};

const UserInfo: Component<UserInfoProps> = (props): JSXElement => {
  let containerRef: HTMLDivElement | undefined;

  onMount(() => {
    let cancelled = false;
    customElements.whenDefined('modus-wc-avatar').then(() => {
      if (cancelled || !containerRef) return;
      injectInRenderContext(
        containerRef,
        'mc-user-info-style',
        MODUS_USER_INFO_STYLESHEET
      );
    });
    onCleanup(() => {
      cancelled = true;
    });
  });

  const firstName = (): string => props.user?.firstName ?? '--';
  const lastName = (): string => props.user?.lastName ?? '--';
  const email = (): string => props.user?.email ?? '--';
  const imgSize = (): string => props.size ?? 'small';
  const avatarSize = (): string => avatarSizeMap[imgSize()] ?? 'sm';
  const imageUrl = (): string => props.user?.thumbnail || props.defaultImg;

  return (
    <div
      ref={(el) => {
        containerRef = el;
      }}
      class="tc-user-info"
      data-cy={props['data-cy'] || 'user-info'}
    >
      <div
        class={`tc-user-info-avatar-wrapper${props.onClick ? ' tc-user-info-avatar-wrapper--clickable' : ''}`}
        onClick={() => props.onClick?.()}
        role={props.onClick ? 'button' : undefined}
      >
        <modus-wc-avatar
          img-src={imageUrl()}
          size={avatarSize() as DaisySize}
          alt={`${firstName()} ${lastName()}`}
        />
      </div>
      <div class="tc-user-info-text">
        <modus-wc-typography hierarchy="h5" size="md" weight="semibold">
          <span class="tc-user-info-truncated">
            {firstName()}&nbsp;{lastName()}
          </span>
        </modus-wc-typography>
        <modus-wc-typography hierarchy="p" size="sm">
          <span class="tc-user-info-truncated tc-user-info-email">
            {email()}
          </span>
        </modus-wc-typography>
      </div>
    </div>
  );
};

export default UserInfo;
